import { useCallback, useEffect, useState } from 'react';
import { fetchAgentMemory } from '../../agents/agentMemoryApi.ts';
import type { AgentStreamEvent, StreamedMemoryRecord } from '../../agents/agentStream.ts';
import type { MemorySnapshotSection } from '../../agents/agentMemoryApi.ts';

/** A `memory-write` frame as it rides the turn stream (op + records). */
type MemoryWriteEvent = Extract<AgentStreamEvent, { type: 'memory-write' }>;

/** Canonical tier order + the headers the `/agent-memory` Lambda emits, so a
 *  tier first seen via a live append renders in the same place + label as a
 *  fetched one. Unknown modules sort last. */
const MODULE_ORDER = ['session', 'episodic', 'semantic'] as const;
const MODULE_HEADERS: Record<string, string> = {
  session: '## Recent turns (this session)',
  episodic: '## Past sessions',
  semantic: '## Known facts',
};

function orderIndex(moduleId: string): number {
  const i = (MODULE_ORDER as readonly string[]).indexOf(moduleId);
  return i === -1 ? MODULE_ORDER.length : i;
}

/**
 * De-dup the current session across tiers, mirroring the server's
 * `filterForSession`: the current session's turns belong under `session`, so they
 * are excluded from `episodic` — otherwise the same turn lists twice (once under
 * "Recent turns", once under "Past sessions"). `semantic` (cross-session facts)
 * passes through. With no session id, nothing is filtered.
 */
function keepForSession(
  moduleId: string,
  records: readonly StreamedMemoryRecord[],
  sessionId: string | undefined,
): StreamedMemoryRecord[] {
  if (sessionId === undefined) {
    return [...records];
  }
  if (moduleId === 'episodic') {
    return records.filter((r) => r.meta?.['sessionId'] !== sessionId);
  }
  if (moduleId === 'session') {
    return records.filter((r) => r.meta?.['sessionId'] === sessionId);
  }
  return [...records];
}

/** Apply one streamed `memory-write` to the tiered sections, returning a new
 *  array (or the same reference when nothing changed) so a live write shows up
 *  without re-polling S3 — keeping the same de-dup the fetch applies. */
function reduceMemoryWrite(
  sections: readonly MemorySnapshotSection[],
  event: MemoryWriteEvent,
  sessionId: string | undefined,
): readonly MemorySnapshotSection[] {
  const { moduleId } = event;

  if (event.op === 'reset') {
    const next = sections.filter((s) => s.moduleId !== moduleId);
    return next.length === sections.length ? sections : next;
  }

  if (event.op === 'delete') {
    const ids = new Set(event.recordIds ?? []);
    if (ids.size === 0) {
      return sections;
    }
    return sections
      .map((s) =>
        s.moduleId === moduleId
          ? { ...s, records: s.records.filter((r) => !ids.has(r.id)) }
          : s,
      )
      .filter((s) => s.records.length > 0);
  }

  const incoming = keepForSession(moduleId, event.records ?? [], sessionId);
  if (incoming.length === 0) {
    return sections;
  }
  const existing = sections.find((s) => s.moduleId === moduleId);
  const have = new Set(existing?.records.map((r) => r.id) ?? []);
  const fresh = incoming.filter((r) => !have.has(r.id));
  if (fresh.length === 0) {
    return sections;
  }
  if (existing) {
    return sections.map((s) =>
      s.moduleId === moduleId ? { ...s, records: [...s.records, ...fresh] } : s,
    );
  }
  const created: MemorySnapshotSection = {
    moduleId,
    header: MODULE_HEADERS[moduleId] ?? moduleId,
    records: fresh,
    trimmed: 0,
  };
  return [...sections, created].sort((a, b) => orderIndex(a.moduleId) - orderIndex(b.moduleId));
}

/**
 * Loads the agent's stored memory (every tier) for the inspector's "Memory › All"
 * view — the knowledge base — from `GET /agent-memory/{agentId}?sessionId=…`. This
 * is a distinct source from the Memory Graph, which projects the per-conversation
 * session log; the "All" list answers "what does the agent know in total?".
 *
 * It shows the **entire** episodic + semantic store, plus the current session's
 * turns — but **session-scoped** so nothing duplicates: the current session's
 * turns appear under `session`, and `episodic` excludes them (it lists the *other*
 * sessions). A failed/unreachable request is non-fatal (the list stays empty).
 *
 * The returned `applyMemoryWrite` keeps the list current as the conversation runs
 * (wire it to {@link useAgentChat}'s `onMemoryWrite`), instead of re-polling S3
 * (its write is fire-and-forget, so an immediate refetch would race the turn).
 */
export function useAgentMemory(
  agentId: string,
  sessionId: string | undefined,
): {
  readonly sections: readonly MemorySnapshotSection[];
  readonly applyMemoryWrite: (event: MemoryWriteEvent) => void;
} {
  const [sections, setSections] = useState<readonly MemorySnapshotSection[]>([]);

  useEffect(() => {
    let cancelled = false;
    setSections([]);
    fetchAgentMemory(agentId, sessionId)
      .then((next) => {
        if (!cancelled) {
          setSections(next);
        }
      })
      .catch(() => {
        // Non-fatal: leave the list empty. fetchAgentMemory already logged.
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, sessionId]);

  const applyMemoryWrite = useCallback(
    (event: MemoryWriteEvent): void => {
      setSections((prev) => reduceMemoryWrite(prev, event, sessionId));
    },
    [sessionId],
  );

  return { sections, applyMemoryWrite };
}
